import { useRef, useEffect, useCallback, useState } from "react"
import { useSelector } from "react-redux"
import { type RootState } from "@/redux/store.ts"

type StreamingState =
  | "idle"
  | "connecting"
  | "connected"
  | "streaming"
  | "completed"
  | "error"

interface StreamingResponse {
  type: "connected" | "status" | "content" | "complete" | "error"
  message?: string
  content?: string
  full_response?: string
}

interface UseStreamingProps {
  onContent: (content: string) => void
  onComplete: (fullResponse: string) => void
  onError: (error: string) => void
  onStatus?: (status: string) => void
  onConnected?: () => void
  maxRetries?: number
  retryDelay?: number
}

interface StreamingConfig {
  threadId: string
  message: string
  suggestedQuestionId?: string
}

const RETRY_DELAYS = [1000, 2000, 4000, 8000]

export const useStreaming = ({
  onContent,
  onComplete,
  onError,
  onStatus,
  onConnected,
  maxRetries = 3,
  retryDelay = 1000,
}: UseStreamingProps) => {
  const [state, setState] = useState<StreamingState>("idle")
  const [retryCount, setRetryCount] = useState(0)

  const abortControllerRef = useRef<AbortController | null>(null)
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
  const configRef = useRef<StreamingConfig | null>(null)
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const BASE_URL = import.meta.env.VITE_API_URL
  const token = useSelector((state: RootState) => state.auth.token)

  const cleanup = useCallback(() => {
    if (readerRef.current) {
      try {
        readerRef.current.cancel()
      } catch (error) {
        console.debug("Error canceling reader (this is normal):", error)
      }
      readerRef.current = null
    }

    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch (error) {
        console.debug("Error aborting controller (this is normal):", error)
      }
      abortControllerRef.current = null
    }

    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
      retryTimeoutRef.current = null
    }

    setState("idle")
  }, [])

  const parseSSEData = useCallback((chunk: string) => {
    const lines = chunk.split("\n").filter((line) => line.trim())
    const events: StreamingResponse[] = []

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6))
          if (data && typeof data === "object" && "type" in data) {
            events.push(data as StreamingResponse)
          }
        } catch (parseError) {
          console.warn("Failed to parse SSE data:", line, parseError)
        }
      }
    }

    return events
  }, [])

  const handleStreamEvent = useCallback(
    (data: StreamingResponse) => {
      switch (data.type) {
        case "connected":
          setState("connected")
          setRetryCount(0)
          onConnected?.()
          break
        case "status":
          setState("streaming")
          onStatus?.(data.message || "")
          break
        case "content":
          setState("streaming")
          onContent(data.content || "")
          break
        case "complete":
          setState("completed")
          onComplete(data.full_response || "")
          cleanup()
          break
        case "error":
          setState("error")
          onError(data.message || "Unknown server error")
          cleanup()
          break
      }
    },
    [onContent, onComplete, onError, onStatus, onConnected, cleanup],
  )

  const retryConnection = useCallback(() => {
    if (retryCount >= maxRetries || !configRef.current) {
      setState("error")
      onError(`Failed to connect after ${maxRetries} attempts`)
      return
    }

    const delay = retryDelay
      ? retryDelay * Math.pow(2, retryCount)
      : RETRY_DELAYS[Math.min(retryCount, RETRY_DELAYS.length - 1)]

    retryTimeoutRef.current = setTimeout(() => {
      setRetryCount((prev) => prev + 1)
      startStreamingInternal(configRef.current!)
    }, delay)
  }, [retryCount, maxRetries, retryDelay, onError])

  const startStreamingInternal = useCallback(
    async (config: StreamingConfig) => {
      try {
        setState("connecting")

        abortControllerRef.current = new AbortController()

        const headers: Record<string, string> = {
          "Content-Type": "application/json",
        }

        if (token) {
          headers["Authorization"] = `Token ${token}`
        }

        const streamingData = {
          message: config.message,
          ...(config.suggestedQuestionId && {
            suggested_question_id: config.suggestedQuestionId,
          }),
        }

        const response = await fetch(
          `${BASE_URL}ai-assistant/${config.threadId}/stream/`,
          {
            method: "POST",
            headers,
            body: JSON.stringify(streamingData),
            signal: abortControllerRef.current.signal,
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        if (!response.body) {
          throw new Error("No response body received")
        }

        const reader = response.body.getReader()
        readerRef.current = reader
        const decoder = new TextDecoder()

        while (true) {
          const { done, value } = await reader.read()

          if (done) break

          const chunk = decoder.decode(value, { stream: true })
          const events = parseSSEData(chunk)

          for (const event of events) {
            handleStreamEvent(event)

            if (event.type === "complete" || event.type === "error") {
              return
            }
          }
        }
      } catch (error: any) {
        // Handle AbortError silently as it's expected when streams are cancelled
        if (error.name === "AbortError") {
          console.debug(
            "Stream was aborted (this is normal when stopping streams)",
          )
          return
        }

        console.error("Streaming error:", error)
        setState("error")

        if (error instanceof TypeError || error.message.includes("fetch")) {
          retryConnection()
        } else {
          onError(`Connection error: ${error.message}`)
          cleanup()
        }
      }
    },
    [
      token,
      BASE_URL,
      parseSSEData,
      handleStreamEvent,
      retryConnection,
      cleanup,
      onError,
    ],
  )

  // Public API
  const startStreaming = useCallback(
    (threadId: string, message: string, suggestedQuestionId?: string) => {
      // Stop any existing stream
      cleanup()

      const config: StreamingConfig = {
        threadId,
        message,
        suggestedQuestionId,
      }

      configRef.current = config
      setRetryCount(0)
      startStreamingInternal(config)
    },
    [cleanup, startStreamingInternal],
  )

  const stopStreaming = useCallback(() => {
    cleanup()
  }, [cleanup])

  const isStreaming = useCallback(() => {
    return (
      state === "connecting" || state === "connected" || state === "streaming"
    )
  }, [state])

  const getState = useCallback(() => state, [state])

  useEffect(() => {
    return cleanup
  }, [cleanup])

  return {
    startStreaming,
    stopStreaming,
    isStreaming,
    getState,
    retryCount,
    state,
  }
}
